/**
 * SubmitProofModal - Shared modal for "Submit PDF Proof", "Submit Print Finals",
 * and "Assembly Finals Completed".
 *
 * All data (PM, project, SKUs) is passed in from the parent — no separate API call needed.
 * Only the submit action hits the backend.
 */

import { useState } from "react";

const API_BASE = import.meta.env.VITE_API_URL || "";

/**
 * Tracker status display — subordinate to email result.
 * No retry button. No system internals.
 */
function TrackerStatusDisplay({ status, message }) {
  const styles = {
    updated:          "text-green-700 bg-green-50",
    already_set:      "text-blue-700 bg-blue-50",
    failed_retryable: "text-gray-600 bg-gray-50",
    not_found:        "text-amber-700 bg-amber-50",
    multiple_matches: "text-amber-700 bg-amber-50",
    failed_permanent: "text-amber-700 bg-amber-50",
  };

  return (
    <div className={`text-sm p-3 rounded-lg ${styles[status] || "text-gray-600 bg-gray-50"}`}>
      <span>{message}</span>
      {status === "failed_retryable" && (
        <span className="text-xs ml-1 opacity-75">(will retry automatically)</span>
      )}
    </div>
  );
}

export default function SubmitProofModal({
  isOpen,
  onClose,
  quoteNumber,
  orderNumber,    // Optional — used for Excel tracker row matching
  projectName,
  customerName,
  pmName,
  jobs = [],       // Array of { jobNumber, name, ... } from the parent's job list
  mode = "pdf-proof", // "pdf-proof" | "print-finals" | "assembly-finals"
  token,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [scope, setScope] = useState("all");
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [notes, setNotes] = useState("");
  const [result, setResult] = useState(null);

  const modeConfig = {
    "pdf-proof": { title: "Submit PDF Proof", endpoint: "/api/pa-queue/submit-pdf-proof", color: "blue", skuLabel: "proof-ready" },
    "print-finals": { title: "Submit Print Finals", endpoint: "/api/pa-queue/submit-print-finals", color: "green", skuLabel: "printed and ready" },
    "assembly-finals": { title: "Assembly Finals Completed", endpoint: "/api/pa-queue/submit-assembly-finals", color: "purple", skuLabel: "assembled and complete" },
  };
  const config = modeConfig[mode] || modeConfig["pdf-proof"];
  const title = config.title;
  const endpoint = config.endpoint;

  // Derive PM email from name (same convention as backend)
  let pmEmail = null;
  if (pmName) {
    const parts = pmName.trim().split(/\s+/);
    if (parts.length >= 2) {
      pmEmail = `${parts[0].toLowerCase()}.${parts[parts.length - 1].toLowerCase()}@3dcolor.com`;
    } else {
      pmEmail = `${parts[0].toLowerCase()}@3dcolor.com`;
    }
  }

  // Build SKU list from jobs
  const skus = jobs.map((j) => ({
    sku: j.jobNumber || j.id || j.Job,
    description: j.name || j.partNumber || "",
  }));

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const body = {
        quoteNumber,
        orderNumber: orderNumber || undefined,
        projectName,
        customerName,
        pmName,
        scope,
        skus: scope === "all" ? skus.map((s) => s.sku) : selectedSkus,
        notes: notes.trim() || null,
      };

      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Submission failed");
      }

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkuToggle = (sku) => {
    setSelectedSkus((prev) =>
      prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]
    );
  };

  const handleClose = () => {
    // Reset state on close
    setScope("all");
    setSelectedSkus([]);
    setNotes("");
    setError(null);
    setResult(null);
    onClose();
  };

  if (!isOpen) return null;

  const isValid = scope === "all" || selectedSkus.length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div
          className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={`px-6 py-4 border-b border-gray-200 ${config.color === "blue" ? "bg-blue-50" : config.color === "green" ? "bg-green-50" : "bg-purple-50"}`}>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Quote #{quoteNumber}
              {orderNumber && <span className="font-semibold text-gray-700"> / Order #{orderNumber}</span>}
              {projectName && ` - ${projectName}`}
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-4 overflow-y-auto flex-1">
            {/* Success result */}
            {result && (
              <div className="space-y-3">
                {/* Primary: Email result (always dominant) */}
                {result.emailSent ? (
                  <div className="flex items-center gap-2 text-green-700 bg-green-50 p-3 rounded-lg">
                    <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">
                      Notification sent to {result.pmName}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded-lg">
                    <svg className="h-5 w-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="font-medium">
                      Email could not be sent. Please notify the PM manually.
                    </span>
                  </div>
                )}

                {result.warnings && result.warnings.length > 0 && !result.unverified && (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-300 p-3 rounded-lg">
                    <strong>Data was corrected:</strong> The system detected a mismatch and used the verified database values instead of what was shown on screen.
                  </div>
                )}

                {/* Secondary: Tracker status (only shown when tracker is enabled) */}
                {result.tracker?.message && (
                  <TrackerStatusDisplay status={result.tracker.status} message={result.tracker.message} />
                )}

                <button
                  onClick={handleClose}
                  className="w-full mt-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            )}

            {/* Error */}
            {error && !result && (
              <div className="text-red-600 bg-red-50 p-3 rounded-lg text-sm mb-4">
                {error}
              </div>
            )}

            {/* Form */}
            {!result && (
              <>
                {/* PM Info */}
                <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">PM:</span>
                    <span className="font-medium">{pmName || "Not assigned"}</span>
                  </div>
                  {customerName && (
                    <div className="flex justify-between mt-1">
                      <span className="text-gray-500">Customer:</span>
                      <span className="font-medium">{customerName}</span>
                    </div>
                  )}
                </div>

                {!pmEmail && (
                  <div className="text-amber-700 bg-amber-50 p-3 rounded-lg text-sm mb-4">
                    No PM assigned to this project. Cannot send notification.
                  </div>
                )}

                {/* SKU selection */}
                <p className="text-sm text-gray-600 mb-3 font-medium">
                  Which SKUs are {config.skuLabel}?
                </p>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="scope"
                      value="all"
                      checked={scope === "all"}
                      onChange={() => setScope("all")}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="font-medium text-gray-900">
                      ALL SKUs ({skus.length} total)
                    </span>
                  </label>

                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="scope"
                      value="selected"
                      checked={scope === "selected"}
                      onChange={() => setScope("selected")}
                      className="h-4 w-4 mt-1 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="font-medium text-gray-900">Select specific SKUs:</span>
                  </label>

                  {scope === "selected" && (
                    <div className="ml-7 space-y-2 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-2">
                      {skus.length === 0 ? (
                        <p className="text-sm text-gray-500 italic p-2">No SKUs found</p>
                      ) : (
                        skus.map((sku, i) => (
                          <label
                            key={i}
                            className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedSkus.includes(sku.sku)}
                              onChange={() => handleSkuToggle(sku.sku)}
                              className="h-4 w-4 text-purple-600 rounded focus:ring-purple-500"
                            />
                            <span className="text-sm font-mono text-gray-800">{sku.sku}</span>
                            {sku.description && (
                              <span className="text-sm text-gray-500 truncate">- {sku.description}</span>
                            )}
                          </label>
                        ))
                      )}
                    </div>
                  )}
                </div>

                {/* Notes */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Any additional context for the PM..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {!result && (
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={handleClose}
                disabled={submitting}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={!isValid || submitting || !pmEmail}
                className={`px-4 py-2 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                  config.color === "blue" ? "bg-blue-600 hover:bg-blue-700"
                    : config.color === "green" ? "bg-green-600 hover:bg-green-700"
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {submitting && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {submitting ? "Sending..." : title}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
